import React, { useState } from "react";
import "./NewSetupPage.scss";
import {
    Button,
    ButtonProps,
    Grid,
    OutlinedInputProps,
    TextField,
    TextFieldProps,
    InputLabel,
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PlayCircleOutlineTwoToneIcon from "@mui/icons-material/PlayCircleOutlineTwoTone";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory } from "react-router-dom";
import RoutesName from "routes/routesName";
import Header from "../../components/AppHeader";
import Footer from "../../components/AppFooter";
import { useDispatch } from "react-redux";
import { setCurrentScene, setSceneName } from "state/slices/SceneSlice";
import scene from "assets/json/scenes.json";
import { SelectChangeEvent } from "@mui/material";

const RedditTextField = styled((props: TextFieldProps) => (
    <TextField InputProps={{ disableUnderline: true } as Partial<OutlinedInputProps>} {...props} />
))(({ theme }) => ({
    "& .MuiFormControl-root": {
        marginTop: 0,
    },
    label: {
        color: "#02334E",
    },
    "& label.Mui-focused": {
        color: "#02334E",
    },
    "& .MuiInputBase-input": {
        color: "#02334E",
    },
    "& .MuiFilledInput-root": {
        border: "1px solid #e2e2e1",
        overflow: "hidden",
        borderRadius: 4,
        backgroundColor: theme.palette.mode === "light" ? "#fcfcfb" : "#2b2b2b",
        transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
        "&:hover": {
            backgroundColor: "transparent",
        },
        "&.Mui-focused": {
            backgroundColor: "transparent",
            // boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 2px`,
            borderColor: "#E6117B",
        },
    },
}));

const ColorButton = styled(Button)<ButtonProps>(() => ({
    boxShadow: "none",
    textTransform: "none",
    fontSize: 14,
    fontWeight: "normal",
    border: "1px solid",
    color: "#fff",
    borderRadius: "50px",
    lineHeight: 1.5,
    backgroundColor: "#E6117B",
    borderColor: "#E6117B",
    padding: "10px",
    fontFamily: [
        "Open Sans",
        "-apple-system",
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
    ].join(","),
    "&:hover": {
        backgroundColor: "#BE0F66",
        borderColor: "#BE0F66",
        boxShadow: "none",
        color: "#fff",
    },
    "&:active": {
        boxShadow: "none",
        backgroundColor: "#BE0F66",
        borderColor: "#BE0F66",
    },
}));

const NewSetupPage = (): JSX.Element => {
    const router = useHistory();
    const dispatch = useDispatch();
    // Define the state
    const [name, setName] = useState<string>("");
    const [width, setWidth] = useState<number>(8);
    const [depth, setDepth] = useState<number>(8);
    const [nameError, setNameError] = useState<boolean>(false);

    /**
     * Navigate to page.
     * @param type
     */
    const handleProceed = (type: string) => {
        if (type === "start") {
            if (name.length <= 0) {
                setNameError(true);
                return;
            }

            // Initialize setup scene object.
            const currentScene = {
                ...scene.preBuiltScenes.New,
                name: name,
                roomSize: {
                    width: width,
                    length: depth,
                    height: 3.0,
                },
            };

            dispatch(setCurrentScene(currentScene));
            dispatch(setSceneName(name));
            // Begin cmr page
            router.push({ pathname: RoutesName.modelPage, state: { newScene: true } });

            document.body.classList.add("newPage");
        }
        if (type === "cancel") {
            // Go back to previous page
            router.push({ pathname: RoutesName.home });
        }
    };

    /**
     * Update state based on input type.
     * @param event
     */
    const handleUpdate = (event: SelectChangeEvent<string>) => {
        const value: string = event.target.value;
        switch (value) {
            case "small":
                setWidth(4);
                setDepth(4);
                break;
            case "medium":
                setWidth(8);
                setDepth(8);
                break;
            case "large":
                setWidth(12);
                setDepth(12);
                break;
            default:
                setWidth(4);
                setDepth(4);
        }
    };

    const handleNameUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value: string = event.target.value;
        if (value.length <= 0) {
            setNameError(true);
        } else {
            setNameError(false);
        }
        setName(value);
    };

    return (
        <div className="newSetupPage">
            <Header />
            <div className="contentCenter fullWidthHeight">
                <section className="newSetupCard">
                    <header className="cardHeader">
                        <h2>New Setup</h2>
                    </header>
                    <article className="cardBody">
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <RedditTextField
                                    autoComplete="off"
                                    error={nameError}
                                    onChange={handleNameUpdate}
                                    helperText={nameError && "Name is required"}
                                    name="name"
                                    value={name}
                                    fullWidth
                                    label="Name"
                                    id="name"
                                    variant="filled"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="room-select-label">Select Room Size</InputLabel>
                                    <Select
                                        labelId="room-select-label"
                                        id="room-select"
                                        onChange={handleUpdate}
                                    >
                                        <MenuItem value="small">Small</MenuItem>
                                        <MenuItem value="medium">Medium</MenuItem>
                                        <MenuItem value="large">Large</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <ColorButton
                                    onClick={() => handleProceed("cancel")}
                                    fullWidth={true}
                                    variant="contained"
                                    className="cancel"
                                    startIcon={<CloseIcon className="icon" />}
                                >
                                    Cancel
                                </ColorButton>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ColorButton
                                    onClick={() => handleProceed("start")}
                                    fullWidth={true}
                                    variant="contained"
                                    className="start"
                                    startIcon={<PlayCircleOutlineTwoToneIcon className="icon" />}
                                >
                                    Start
                                </ColorButton>
                            </Grid>
                        </Grid>
                    </article>
                </section>
            </div>
            <Footer />
        </div>
    );
};
export default NewSetupPage;
